import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import special from "../styles/special.module.scss";
import { UserContext } from "../context/UserContext";
import { buildApiUrl } from "../utilities/Static";
import { trackEvent } from "../utilities/analytics";
import {
  hasPersonalAccountEmail,
  identifierLooksLikeEmail,
} from "../utilities/loginAccount";

const FEEDBACK_TOPICS = [
  { id: "feedback", label: "Feedback" },
  { id: "issue", label: "Issues" },
  { id: "bug", label: "Bug Report" },
];

export function feedbackMailSubject(topic, subject) {
  const label =
    FEEDBACK_TOPICS.find((item) => item.id === topic)?.label || "Feedback";
  return `[${label}] ${String(subject || "").trim()}`.slice(0, 180);
}

function Contact() {
  const { userInformation } = useContext(UserContext);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [topic, setTopic] = useState("feedback");
  const [received, setReceived] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginRequest = searchParams.get("request") === "login";
  const isFeedback = Boolean(userInformation?.isLoggedIn) && !loginRequest;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [received]);

  useEffect(() => {
    if (!userInformation?.isLoggedIn) {
      return;
    }

    setName((current) => {
      if (current) {
        return current;
      }
      const raw = String(userInformation.userName || "").trim();
      if (!raw) {
        return current;
      }
      return identifierLooksLikeEmail(raw) ? raw.split("@")[0] : raw;
    });

    setEmail((current) => {
      if (current || !hasPersonalAccountEmail(userInformation)) {
        return current;
      }
      return String(userInformation.email || "").trim();
    });
  }, [userInformation]);

  useEffect(() => {
    if (!loginRequest) {
      return;
    }
    setSubject((current) => current || "Request a login");
  }, [loginRequest]);

  const contactVisualStyle = {
    backgroundImage: `linear-gradient(180deg, rgba(8, 12, 12, 0.12), rgba(8, 12, 12, 0.84)), url(${process.env.PUBLIC_URL}/images/contact.jpg)`,
  };

  const URL_Mail = buildApiUrl("/mail/contact");

  const handleContact = () => {
    const mailSubject = isFeedback
      ? feedbackMailSubject(topic, subject)
      : subject;

    fetch(URL_Mail, {
      method: "POST",
      body: JSON.stringify({
        name,
        email,
        subject: mailSubject,
        message,
      }),
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.message === "ok") {
          setReceived(true);
          if (isFeedback) {
            trackEvent("feedback_submit", { topic });
          } else {
            trackEvent("contact_submit");
          }
        }
        console.log(res);
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const visualEyebrow = isFeedback ? "Feedback" : "Material library";
  const visualTitle = isFeedback
    ? "Help us improve Matlib."
    : "Bring your next project into focus.";
  const visualBody = isFeedback
    ? "Report a bug, flag something that is in the way, or suggest a change. These notes come straight through."
    : "Use this form for sourcing questions, platform support, or help building a better collection.";

  if (received) {
    return (
      <div className={special.contactShell}>
        <div className={special.contactPage}>
          <div className={special.contactBackdrop}></div>
          <div className={special.contactFrame}>
            <div className={special.contactVisual} style={contactVisualStyle}>
              <div className={special.contactVisualOverlay}></div>
              <div className={special.contactVisualCopy}>
                <p className={special.contactVisualEyebrow}>{visualEyebrow}</p>
                <h2>
                  {isFeedback
                    ? "Thanks for the note."
                    : "Tell us what you are building."}
                </h2>
                <p>
                  {isFeedback
                    ? "Your feedback is in the inbox. We will follow up if we need more detail."
                    : "Questions about sourcing, swatches, or account access all go through the same channel."}
                </p>
                <div className={special.contactLogoRow}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/asiatanLogo.png`}
                    alt="Asiatan logo"
                    className={special.contactLogo}
                  />
                  <img
                    src={`${process.env.PUBLIC_URL}/images/tectuffLogoWhite.png`}
                    alt="Tectuff logo"
                    className={special.contactLogo}
                  />
                </div>
              </div>
            </div>

            <div className={special.contactForm}>
              <h1>Message sent</h1>
              <p className={special.contactIntro}>
                {isFeedback
                  ? "Thanks for helping improve Matlib."
                  : "Thanks for reaching out. We will get back to you shortly."}
              </p>

              <div className={special.contactActions}>
                <button
                  className={special.contactButton}
                  type="button"
                  onClick={() => navigate("/library")}
                >
                  Go back to library
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={special.contactShell}>
      <div className={special.contactPage}>
        <div className={special.contactBackdrop}></div>
        <div className={special.contactFrame}>
          <div className={special.contactVisual} style={contactVisualStyle}>
            <div className={special.contactVisualOverlay}></div>
            <div className={special.contactVisualCopy}>
              <p className={special.contactVisualEyebrow}>{visualEyebrow}</p>
              <h2>{visualTitle}</h2>
              <p>{visualBody}</p>
              <div className={special.contactLogoRow}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/asiatanLogo.png`}
                  alt="Asiatan logo"
                  className={special.contactLogo}
                />
                <img
                  src={`${process.env.PUBLIC_URL}/images/tectuffLogoWhite.png`}
                  alt="Tectuff logo"
                  className={special.contactLogo}
                />
              </div>
            </div>
          </div>

          <div className={special.contactForm}>
            <h1>{isFeedback ? "Send feedback" : "Contact MATLIB"}</h1>
            <p className={special.contactIntro}>
              {isFeedback
                ? "Pick a type, then tell us what you noticed. Include a material name or page if it helps."
                : "If you have questions about sourcing, swatches, or account access, please fill out the form below and we will get back to you shortly."}
            </p>

            {isFeedback ? (
              <div className={special.contactInputGroup}>
                <span>Type</span>
                <div className={special.contactTopicRow} role="group" aria-label="Feedback type">
                  {FEEDBACK_TOPICS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`${special.contactTopic} ${
                        topic === item.id ? special.contactTopicActive : ""
                      }`}
                      onClick={() => setTopic(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className={special.contactInputGroup}>
              <label htmlFor="contact-name">Name</label>
              <input
                id="contact-name"
                type="text"
                onChange={(e) => setName(e.target.value)}
                value={name}
                placeholder="Your name"
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="contact-email">Email</label>
              <input
                id="contact-email"
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                placeholder="you@company.com"
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="contact-subject">Subject</label>
              <input
                id="contact-subject"
                type="text"
                onChange={(e) => setSubject(e.target.value)}
                value={subject}
                placeholder={
                  isFeedback ? "Short description" : "What can we help with?"
                }
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="contact-message">Message</label>
              <textarea
                id="contact-message"
                onChange={(e) => setMessage(e.target.value)}
                value={message}
                placeholder={
                  isFeedback
                    ? "What happened, or what would you change?"
                    : "Tell us what you need"
                }
              />
            </div>

            <div className={special.contactActions}>
              <button
                className={special.contactButton}
                type="button"
                onClick={handleContact}
              >
                {isFeedback ? "Send feedback" : "Send Email"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
